import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { NOTE_MAX_LENGTH, STAGE_LABELS, STAGE_ORDER, reissueBlocker } from '@ai-interview/shared';
import type {
  CompetencyScore,
  HiringDecision,
  Interview,
  InterviewNote,
  InterviewReport,
  InterviewStage,
  InterviewStatus,
  Job,
  Recommendation,
} from '@ai-interview/shared';
import { api } from '../lib/api';
import { InviteLink } from '../components/InviteLink';
import {
  DecisionBadge,
  ErrorNotice,
  RecommendationBadge,
  StatusBadge,
} from '../components/Status';

/** Why there is no scorecard, for each status that has none. */
const NO_SCORECARD_YET: Partial<Record<InterviewStatus, string>> = {
  invited: 'The candidate has not started their interview yet.',
  in_progress: 'This interview is under way. The transcript and scorecard appear here when it ends.',
  completed: 'The interview finished and scoring is queued. The scorecard appears here when it is ready.',
  scoring_failed: 'Scoring did not complete. The transcript is safe — you can run it again.',
  expired:
    'The invite link expired before the interview was taken. Issue a new link below to give the candidate another chance.',
  abandoned:
    'The candidate started but the call never finished - they may have closed the tab or lost their connection. Issue a new link below to let them try again.',
  revoked: 'The invite link was revoked. Issue a new link below if the candidate should still interview.',
};

/** Statuses where the link, rather than a result, is what the recruiter has to act on. */
const LINK_STATUSES: InterviewStatus[] = ['invited', 'in_progress', 'expired', 'abandoned', 'revoked'];

/** How often the report re-reads while it is waiting on the interview or its score. */
const POLL_INTERVAL_MS = 8000;

/** A re-score that has changed nothing after this long is not coming; stop asking. */
const RESCORE_WAIT_MS = 10 * 60 * 1000;

type ReportData = InterviewReport;

interface PendingRescore {
  /** The interview row's `updatedAt` when the re-score was requested. */
  updatedAt: string;
  requestedAt: number;
}

/**
 * Whether something is still on its way to this page.
 *
 * A live or just-finished interview is waiting on its transcript or score. A
 * re-score may not change the status at all - a scored interview stays scored -
 * so the signal there is the interview row being written again, which the
 * scorer does on success and on failure alike.
 */
function isWaiting(data: ReportData, rescore: PendingRescore | undefined): boolean {
  const { status, updatedAt } = data.interview;
  if (status === 'in_progress' || status === 'completed') return true;
  return (
    rescore !== undefined &&
    updatedAt === rescore.updatedAt &&
    Date.now() - rescore.requestedAt < RESCORE_WAIT_MS
  );
}

export function InterviewReportPage() {
  const { interviewId } = useParams<{ interviewId: string }>();
  const [data, setData] = useState<ReportData>();
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [rescore, setRescore] = useState<PendingRescore>();

  const refresh = useCallback(async () => {
    if (!interviewId) return;
    try {
      setData(await api.getInterview(interviewId));
      setError(undefined);
    } catch (err) {
      setError(err);
    }
  }, [interviewId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const waiting = data ? isWaiting(data, rescore) : false;

  // The page used to promise it would update when the score landed and then
  // never looked again. It re-reads on a timer while something is on its way,
  // and a hidden tab waits until it is looked at rather than polling all
  // afternoon in the background. Every read produces new `data` (or a new
  // error), which schedules the next one.
  useEffect(() => {
    if (!waiting) return;
    const poll = () => {
      if (document.hidden) {
        document.addEventListener('visibilitychange', poll, { once: true });
        return;
      }
      void refresh();
    };
    const timer = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [waiting, data, error, refresh]);

  async function requestRescore() {
    if (!interviewId || !data) return;
    setBusy(true);
    try {
      await api.rescore(interviewId);
      setRescore({ updatedAt: data.interview.updatedAt, requestedAt: Date.now() });
      setError(undefined);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return (
      <div className="stack">
        <ErrorNotice error={error} />
        {!error ? <p className="muted">Loading interview…</p> : null}
      </div>
    );
  }

  const { interview, job, transcript, scorecard } = data;
  const rescoring = busy || (rescore !== undefined && waiting);

  return (
    <div className="stack">
      <div className="between">
        <div>
          <h1>{interview.candidate.name}</h1>
          <p className="muted">
            {job?.title ?? 'Role removed'} · {interview.candidate.email}
            {interview.durationSeconds
              ? ` · ${Math.round(interview.durationSeconds / 60)} min call`
              : ''}
          </p>
        </div>
        <div className="row">
          <StatusBadge status={interview.status} />
          {job ? <Link to={`/jobs/${job.id}`}>Back to role</Link> : null}
        </div>
      </div>

      <ErrorNotice error={error} />

      {interview.status === 'invited' || interview.status === 'in_progress' ? null : (
        <DecisionCard
          interview={interview}
          recommendation={scorecard?.recommendation}
          onChanged={(updated) => setData((current) => current && { ...current, interview: updated })}
        />
      )}

      {scorecard ? (
        <>
          <div className="card stack">
            <div className="between">
              <h2>Scorecard</h2>
              <div className="row">
                <RecommendationBadge value={scorecard.recommendation} />
                <span className="badge">{scorecard.overall}/100</span>
                <span className="badge">
                  {Math.round(scorecard.coverage * 100)}% of questions covered
                </span>
              </div>
            </div>

            <p>{scorecard.summary}</p>

            <p className="small muted">
              Generated by {scorecard.modelId} on{' '}
              {new Date(scorecard.scoredAt).toLocaleString()}. This is a screening aid — read the
              transcript before making a decision.
            </p>
          </div>

          <div className="card stack">
            <h2>Competencies</h2>
            {/* Grouped by interview section, each with its average, so a strong
                technical candidate with weak communication shows at a glance. */}
            {competenciesBySection(scorecard.competencyScores, job).map((section) => (
              <div key={section.key} className="stack" style={{ gap: '0.75rem' }}>
                <div className="between">
                  <h3 className="stage-heading">{section.label}</h3>
                  <span className="badge">{averageScore(section.entries)}/5 average</span>
                </div>
                {section.entries.map((entry) => (
                  <div key={entry.competency} className="stack" style={{ gap: '0.35rem' }}>
                    <div className="between">
                      <strong>{entry.competency}</strong>
                      <span className="muted small">{entry.score}/5</span>
                    </div>
                    <div className="score-bar">
                      <span style={{ width: `${(entry.score / 5) * 100}%` }} />
                    </div>
                    <p className="small muted">{entry.evidence}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="grid">
            <ListCard title="Strengths" items={scorecard.strengths} />
            <ListCard title="Concerns" items={scorecard.concerns} />
            <ListCard title="Verify before advancing" items={scorecard.redFlags} tone="negative" />
            <ListCard title="Ask in the next round" items={scorecard.followUpQuestions} />
          </div>
        </>
      ) : (
        <div className="card stack">
          <h2>No scorecard yet</h2>
          <p className="muted">
            {NO_SCORECARD_YET[interview.status] ?? 'This candidate has not completed their interview yet.'}
          </p>
          {transcript ? (
            <div className="row">
              <button onClick={() => void requestRescore()} disabled={rescoring}>
                {rescoring ? 'Scoring…' : 'Score this interview'}
              </button>
            </div>
          ) : null}
          {waiting ? <p className="small muted">Checking for updates…</p> : null}
        </div>
      )}

      {!transcript && LINK_STATUSES.includes(interview.status) ? (
        <InviteLinkCard interview={interview} job={job} onChanged={() => void refresh()} />
      ) : null}

      <NotesCard
        interviewId={interview.id}
        notes={data.notes ?? []}
        onAdded={(note) =>
          setData((current) => current && { ...current, notes: [...(current.notes ?? []), note] })
        }
      />

      {transcript ? (
        <div className="card stack">
          <div className="between">
            <h2>Transcript</h2>
            {scorecard ? (
              <button className="secondary" onClick={() => void requestRescore()} disabled={rescoring}>
                {rescoring ? 'Re-scoring…' : 'Re-score'}
              </button>
            ) : null}
          </div>
          <div className="transcript" style={{ maxHeight: 'none' }}>
            {transcript.turns.map((turn, index) => (
              <div key={index} className="turn">
                <div className="who">{turn.role === 'agent' ? 'Interviewer' : 'Candidate'}</div>
                <div>{turn.message}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const DECISION_ACTIONS: { outcome: HiringDecision; label: string }[] = [
  { outcome: 'advance', label: 'Advance' },
  { outcome: 'hold', label: 'Hold' },
  { outcome: 'reject', label: 'Reject' },
];

/** A decision that goes the other way from a clear AI recommendation. "Hold" and "Maybe" never do. */
function goesAgainst(outcome: HiringDecision, recommendation: Recommendation): boolean {
  return (
    (outcome === 'advance' && recommendation === 'no') ||
    (outcome === 'reject' && (recommendation === 'yes' || recommendation === 'strong_yes'))
  );
}

/**
 * The recruiter's call. The AI recommendation sits beside it for reference, and
 * a decision that goes the other way is pointed out - over many interviews, how
 * often that happens is what says how far to trust the scorecard.
 */
function DecisionCard({
  interview,
  recommendation,
  onChanged,
}: {
  interview: Interview;
  recommendation?: Recommendation;
  onChanged: (interview: Interview) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const decision = interview.decision;

  async function decide(outcome: HiringDecision | null) {
    setBusy(true);
    setError(undefined);
    try {
      onChanged((await api.decide(interview.id, outcome)).interview);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card stack">
      <div className="between">
        <h2>Decision</h2>
        {recommendation ? (
          <span className="small muted">
            AI recommendation <RecommendationBadge value={recommendation} />
          </span>
        ) : null}
      </div>
      <ErrorNotice error={error} />
      <div className="row" role="group" aria-label="Hiring decision">
        {DECISION_ACTIONS.map(({ outcome, label }) => {
          const chosen = decision?.outcome === outcome;
          return (
            <button
              key={outcome}
              className={chosen ? undefined : 'secondary'}
              aria-pressed={chosen}
              disabled={busy || chosen}
              onClick={() => void decide(outcome)}
            >
              {label}
            </button>
          );
        })}
        {decision ? (
          <button className="secondary" disabled={busy} onClick={() => void decide(null)}>
            Clear
          </button>
        ) : null}
      </div>
      <p className="small muted" style={{ margin: 0 }} aria-live="polite">
        {decision ? (
          <>
            <DecisionBadge value={decision.outcome} /> by {decision.decidedBy.email ?? 'a recruiter'},{' '}
            {new Date(decision.decidedAt).toLocaleString()}.
            {recommendation && goesAgainst(decision.outcome, recommendation)
              ? ' This goes against the AI recommendation - worth a note on why.'
              : ''}
          </>
        ) : (
          'No decision yet. The scorecard is a screening aid; the call is yours.'
        )}
      </p>
    </div>
  );
}

/** Notes are added and never edited, so they read as a record of what the team thought, and when. */
function NotesCard({
  interviewId,
  notes,
  onAdded,
}: {
  interviewId: string;
  notes: InterviewNote[];
  onAdded: (note: InterviewNote) => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();

  async function add(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      onAdded(await api.addNote(interviewId, text.trim()));
      setText('');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={add}>
      <h2>Notes</h2>
      {notes.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          No notes yet.
        </p>
      ) : (
        <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0, gap: '0.6rem' }}>
          {notes.map((note) => (
            <li key={note.id} className="turn">
              {/* The transcript's label style, minus the capitals, which would shout an email address. */}
              <div className="who" style={{ textTransform: 'none', letterSpacing: 'normal' }}>
                {note.author.email ?? 'A recruiter'} · {new Date(note.createdAt).toLocaleString()}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{note.text}</div>
            </li>
          ))}
        </ul>
      )}
      <ErrorNotice error={error} />
      <div>
        <label htmlFor="note">Add a note</label>
        <textarea
          id="note"
          maxLength={NOTE_MAX_LENGTH}
          placeholder="What stood out, what to check in the next round…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      <div className="row">
        <button type="submit" disabled={busy || !text.trim()}>
          {busy ? 'Adding…' : 'Add note'}
        </button>
        <span className="small muted">Everyone on the hiring team can see notes. They cannot be edited.</span>
      </div>
    </form>
  );
}

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

function describeLink(interview: Interview): string {
  switch (interview.status) {
    case 'invited':
      return `Valid until ${new Date(interview.expiresAt).toLocaleString()}`;
    case 'expired':
      return `Expired ${new Date(interview.expiresAt).toLocaleString()}`;
    case 'in_progress':
      return interview.startedAt ? `Started at ${time(interview.startedAt)}` : 'Started';
    case 'abandoned':
      return 'Started, never finished';
    case 'revoked':
      return 'Revoked';
    default:
      return '';
  }
}

/**
 * Replace or withdraw the candidate's link without creating a second candidate
 * row. Reissuing retires every earlier link; the server refuses a call that
 * could still be live, and the same rule greys the button out here.
 */
function InviteLinkCard({
  interview,
  job,
  onChanged,
}: {
  interview: Interview;
  job?: Job;
  onChanged: () => void;
}) {
  const [expiresInHours, setExpiresInHours] = useState(24 * 7);
  const [inviteUrl, setInviteUrl] = useState<string>();
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [busy, setBusy] = useState<'reissue' | 'revoke'>();
  const [error, setError] = useState<unknown>();

  const blocker = job
    ? reissueBlocker(interview, job.durationMinutes)
    : { reason: 'The role for this interview no longer exists, so it cannot be reissued.' };
  const roleClosed = job !== undefined && job.status !== 'open';
  const canRevoke = ['invited', 'expired', 'abandoned'].includes(interview.status);

  async function reissue() {
    setBusy('reissue');
    setError(undefined);
    try {
      const result = await api.reissueInvite(interview.id, expiresInHours);
      setInviteUrl(result.inviteUrl);
      setConfirmingRevoke(false);
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(undefined);
    }
  }

  async function revoke() {
    setBusy('revoke');
    setError(undefined);
    try {
      await api.revokeInvite(interview.id);
      setInviteUrl(undefined);
      setConfirmingRevoke(false);
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <div className="card stack">
      <div className="between">
        <h2>Invite link</h2>
        <span className="muted small">{describeLink(interview)}</span>
      </div>
      <ErrorNotice error={error} />
      {inviteUrl ? <InviteLink url={inviteUrl} /> : null}

      <p className="small muted" style={{ margin: 0 }}>
        A new link replaces every earlier link for this candidate - older links stop working. It is
        shown once, like the first.
      </p>
      {blocker ? (
        <p className="small" style={{ margin: 0 }}>
          {blocker.reason}
          {blocker.until ? ` You can issue a new link after ${time(blocker.until)}.` : ''}
        </p>
      ) : null}
      {roleClosed && job ? (
        <p className="small" style={{ margin: 0 }}>
          This role is {job.status}. Reopen it to issue a new link.
        </p>
      ) : null}

      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div style={{ flex: '0 1 150px' }}>
          <label htmlFor="reissue-expiry">Link valid (hours)</label>
          <input
            id="reissue-expiry"
            type="number"
            min={1}
            max={720}
            value={expiresInHours}
            onChange={(e) => setExpiresInHours(Number(e.target.value))}
          />
        </div>
        <button
          onClick={() => void reissue()}
          disabled={Boolean(blocker) || roleClosed || busy !== undefined}
        >
          {busy === 'reissue' ? 'Issuing…' : 'Issue new link'}
        </button>
        {canRevoke && !confirmingRevoke ? (
          <button className="secondary" onClick={() => setConfirmingRevoke(true)} disabled={busy !== undefined}>
            Revoke link
          </button>
        ) : null}
      </div>

      {canRevoke && confirmingRevoke ? (
        <div className="notice stack" role="alertdialog" aria-label="Revoke this link?">
          <span className="small">
            Revoke this link? It stops working immediately. You can still issue a new one later.
          </span>
          <div className="row">
            <button className="danger" onClick={() => void revoke()} disabled={busy !== undefined}>
              {busy === 'revoke' ? 'Revoking…' : 'Revoke link'}
            </button>
            <button className="secondary" onClick={() => setConfirmingRevoke(false)}>
              Keep it
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ListCard({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone?: 'negative';
}) {
  return (
    <div className="card stack">
      <h3 className={tone === 'negative' && items.length > 0 ? 'negative' : undefined}>{title}</h3>
      {items.length === 0 ? (
        <p className="muted small">Nothing noted.</p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
          {items.map((item, index) => (
            <li key={index} className="small">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Groups competency scores under the interview section their questions belong
 * to. A competency takes the section of the first question that names it;
 * one no question names (or a scorecard from before sections existed) lands in
 * "Other" rather than disappearing.
 */
function competenciesBySection(
  scores: CompetencyScore[],
  job?: Job,
): { key: string; label: string; entries: CompetencyScore[] }[] {
  const sectionOf = new Map<string, InterviewStage>();
  for (const question of job?.questions ?? []) {
    if (!sectionOf.has(question.competency)) {
      sectionOf.set(question.competency, question.stage ?? 'technical');
    }
  }
  const sections: { key: string; label: string; entries: CompetencyScore[] }[] = STAGE_ORDER.map(
    (stage) => ({
      key: stage,
      label: STAGE_LABELS[stage],
      entries: scores.filter((entry) => sectionOf.get(entry.competency) === stage),
    }),
  );
  const other = scores.filter((entry) => !sectionOf.has(entry.competency));
  if (other.length > 0) sections.push({ key: 'other', label: 'Other', entries: other });
  return sections.filter((section) => section.entries.length > 0);
}

function averageScore(entries: CompetencyScore[]): string {
  const total = entries.reduce((sum, entry) => sum + entry.score, 0);
  return (total / Math.max(1, entries.length)).toFixed(1);
}
